/**
 * CRM table cell display resolution for foreign-key fields.
 *
 * GraphQL list queries often return only scalar FK ids (e.g. `user_id`) without
 * nested relations or denormalized labels. These resolvers MUST fall back to the
 * raw scalar so Smart*Display components can hydrate from the global lookup cache.
 *
 * Guarded by:
 *   - tests/utils/crm/getFieldValue.test.ts
 *   - tests/utils/entity-user-field-display-coverage.test.ts (build)
 */

import type { BaseEntity, FieldValue } from "@/components/shared/UniversalCRMTable/types";
import type { EntityWithRelations } from "@/lib/types/table/entity-relation-types";

/** User-reference columns resolved through getFieldValue's user branch. */
export const USER_REFERENCE_FIELD_IDS = ["user_id", "created_by", "updated_by"] as const;
export type UserReferenceFieldId = (typeof USER_REFERENCE_FIELD_IDS)[number];

export function isUserReferenceFieldId(fieldId: string): fieldId is UserReferenceFieldId {
  return (USER_REFERENCE_FIELD_IDS as readonly string[]).includes(fieldId);
}

function readScalarId(item: EntityWithRelations<BaseEntity>, fieldId: string): string | undefined {
  const raw = item[fieldId];
  if (typeof raw !== "string") return undefined;
  const trimmed = raw.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

/**
 * Resolve a user FK for table display.
 * Priority: nested relation → denormalized user_name → raw scalar id/email.
 */
export function resolveUserReferenceField<T extends BaseEntity>(
  item: EntityWithRelations<T>,
  fieldId: UserReferenceFieldId
): FieldValue | Record<string, unknown> | undefined {
  const relation = item.app_users;
  if (relation) return relation;

  const scalarId = readScalarId(item, fieldId);
  const userName = item.user_name;
  if (scalarId && userName != null) {
    return { id: scalarId, first_name: userName, last_name: "", email: "" };
  }

  return scalarId;
}

export function resolveAssignedUsers<T extends BaseEntity>(
  item: EntityWithRelations<T>
): FieldValue | Record<string, unknown> | undefined {
  return item.assigned_users ?? undefined;
}

export function resolveAssignedTo<T extends BaseEntity>(
  item: EntityWithRelations<T>
): FieldValue | Record<string, unknown> | undefined {
  if (item.assigned_user) return item.assigned_user;
  return readScalarId(item, "assigned_to");
}

export function resolveContactId<T extends BaseEntity>(
  item: EntityWithRelations<T>
): FieldValue | Record<string, unknown> | undefined {
  const relation = item.contacts ?? item.contact;
  if (relation) return relation;

  const contactId = readScalarId(item, "contact_id");
  const contactName = item.contact_name;
  if (contactId && contactName != null) {
    return { id: contactId, name: contactName, email: item.contact_email ?? "" };
  }

  return contactId;
}

export function resolveCustomerId<T extends BaseEntity>(
  item: EntityWithRelations<T>
): FieldValue | Record<string, unknown> | undefined {
  const relation = item.customer;
  if (relation) return relation;

  const customerId = readScalarId(item, "customer_id");
  const metadata =
    item.metadata && typeof item.metadata === "object"
      ? (item.metadata as Record<string, unknown>)
      : null;
  const metadataName =
    typeof metadata?.customerName === "string" ? metadata.customerName : null;
  const contactName = item.contact_name ?? metadataName;
  if (customerId && contactName != null) {
    return { id: customerId, name: contactName, email: item.contact_email ?? "" };
  }

  return customerId;
}

export function resolveCompanyId<T extends BaseEntity>(
  item: EntityWithRelations<T>
): FieldValue | Record<string, unknown> | undefined {
  const relation = item.company ?? item.companies;
  if (relation) return relation;

  const companyId = readScalarId(item, "company_id");
  const companyName = item.company_name;
  // FTS search rows (search_contacts RPC) carry company_name but NOT company_id,
  // so synthesize a name-only relation object when the id is missing — the cell
  // renderer shows the name (no navigable chip) instead of a blank dash.
  if (companyName != null) {
    return companyId ? { id: companyId, name: companyName } : { name: companyName };
  }

  return companyId;
}

export function resolveTicketId<T extends BaseEntity>(
  item: EntityWithRelations<T>
): FieldValue | Record<string, unknown> | undefined {
  const relation = item.tickets;
  if (relation) return relation;

  const ticketId = readScalarId(item, "ticket_id");
  const ticketNumber = item.ticket_number;
  const ticketTitle = item.ticket_title;
  if (ticketId && (ticketNumber != null || ticketTitle != null)) {
    return { id: ticketId, ticket_number: ticketNumber, title: ticketTitle };
  }

  return ticketId;
}
